import React, { useEffect, useState, useRef } from 'react';
import DesktopSidebar from './DesktopSidebar';
import MobileSidebar from './MobileSidebar';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getNotificationAction } from '../../redux/actions/notificationAction';
import logo from '../../assets/logo.png';
import { Icon } from '../../assets/icons';
import { useTranslation } from 'react-i18next';
import mqttService from '../../service/mqttService'; // Import the centralized MQTT service

function Sidebar({ initLoading }) {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toastIdRef = useRef(null);
  const [messageCount, setMessageCount] = useState(0);

  useEffect(() => {
    // Request notification permission on component mount
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    // This function will be called by the mqttService for each new message
    const handleNewNotification = (payload) => {
      // 1. Dispatch action to refresh notification list/count — keep this running
      // even while muted, so alert counts/badges elsewhere in the app stay accurate.
      dispatch(getNotificationAction());

      if (localStorage.getItem('solyntek_alerts_muted') === '1') return;

      // 2. Show a browser notification (if permission is granted)
      if (Notification.permission === 'granted') {
        new Notification('New Solyntek Alert', {
          body: `New ${payload.model_name} alert from ${payload.camera_name}`,
          icon: logo,
        });
      }

      // 3. Show a toast notification with stacking logic
      const messageContent = `${t('you_have_new_alert')} ${payload.model_name}`;

      if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
        // If a toast is already active, update it
        setMessageCount(prevCount => {
          const newCount = prevCount + 1;
          toast.update(toastIdRef.current, {
            render: `You have ${newCount} new alerts`,
          });
          return newCount;
        });
      } else {
        // Otherwise, create a new toast
        setMessageCount(1);
        toastIdRef.current = toast.warn(
          <div className='flex items-center cursor-pointer'>
            <div>{messageContent}</div>
            <Icon className="ml-2" aria-hidden="true" icon="Warning" />
          </div>,
          {
            onClick: () => navigate('/alerts'),
            onClose: () => {
              toastIdRef.current = null;
              setMessageCount(0);
            },
            autoClose: 5000, // Auto-close after 5 seconds
          }
        );
      }
    };

    // Subscribe to the centralized MQTT service
    const unsubscribe = mqttService.subscribe(handleNewNotification);

    // Unsubscribe when the component unmounts
    return () => {
      unsubscribe();
    };
  }, [dispatch, t, navigate]); // Rerun if dispatch or t changes

  return (
    <>
      <DesktopSidebar initLoading={initLoading} />
      <MobileSidebar />
    </>
  );
}

export default Sidebar;